import React from 'react';
import { Users, TrendingUp, Award } from 'lucide-react';

export interface PyramidNode {
  id: string;
  referralCode: string;
  nickname: string;
  walletAddress: string;
  referredBy?: string;
  vttnBalance: number;
  totalReferrals: number;
  totalVolume: number;
  createdAt: string;
  tier: string;
  isActive: boolean;
}

interface ReferralPyramidProps {
  rootUser: PyramidNode;
  children: PyramidNode[];
  onNodeClick?: (node: PyramidNode) => void;
}

const ReferralPyramid: React.FC<ReferralPyramidProps> = ({
  rootUser,
  children,
  onNodeClick
}) => {
  // 레벨별로 그룹화 (모든 레벨 재귀적으로 수집)
  const groupByLevel = (nodes: PyramidNode[], root: PyramidNode): { [key: number]: PyramidNode[] } => {
    const levels: { [key: number]: PyramidNode[] } = { 0: [root] };
    const referralCodeMap = new Map(nodes.map(n => [n.referralCode, n]));
    const processed = new Set<string>();

    // 재귀적으로 모든 레벨 수집
    const processLevel = (parentCode: string, level: number) => {
      if (level > 20) return; // 최대 20단계
      
      const levelNodes = nodes.filter(n => 
        n.referredBy === parentCode && !processed.has(n.id)
      );

      if (levelNodes.length === 0) return;

      levelNodes.forEach(n => processed.add(n.id));
      
      if (!levels[level]) levels[level] = [];
      levels[level].push(...levelNodes);

      // 다음 레벨 처리
      levelNodes.forEach(node => {
        processLevel(node.referralCode, level + 1);
      });
    };

    processLevel(root.referralCode, 1);

    return levels;
  };

  const levels = groupByLevel(children, rootUser);
  const maxLevel = Math.max(...Object.keys(levels).map(Number));

  const getTierColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'platinum': return 'bg-purple-500';
      case 'gold': return 'bg-yellow-500';
      case 'silver': return 'bg-gray-400';
      default: return 'bg-orange-500';
    }
  };

  const getReferralCountColor = (count: number) => {
    if (count > 100) return 'bg-red-500';
    if (count > 50) return 'bg-orange-500';
    if (count > 10) return 'bg-blue-500';
    return 'bg-green-500';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 overflow-x-auto">
      <div className="flex flex-col items-center space-y-8 min-w-max">
        {Object.keys(levels).map(level => {
          const levelNum = parseInt(level);
          const levelNodes = levels[levelNum];
          const isRoot = levelNum === 0;

          return (
            <div key={level} className="flex flex-col items-center space-y-2">
              <div className="text-sm font-semibold text-gray-600 mb-2">
                레벨 {levelNum} {isRoot ? '(루트)' : `(${levelNodes.length}명)`}
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {levelNodes.map((node, idx) => (
                  <div
                    key={node.id}
                    onClick={() => onNodeClick && onNodeClick(node)}
                    className={`
                      relative rounded-lg p-4 border-2 transition-all cursor-pointer
                      ${isRoot 
                        ? 'bg-gradient-to-br from-purple-500 to-blue-600 text-white border-purple-600 shadow-lg' 
                        : 'bg-white border-gray-300 hover:border-orange-500 hover:shadow-md'
                      }
                      ${node.totalReferrals > 100 ? 'ring-2 ring-red-500' : ''}
                    `}
                    style={{ minWidth: isRoot ? '200px' : '150px' }}
                  >
                    {isRoot && (
                      <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-1">
                        <Award className="w-4 h-4 text-yellow-900" />
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <div className={`w-3 h-3 rounded-full ${getTierColor(node.tier)}`}></div>
                      <div className="font-semibold text-sm truncate">
                        {node.nickname || node.referralCode}
                      </div>
                    </div>
                    
                    <div className="text-xs space-y-1">
                      <div className="font-mono text-gray-600">{node.referralCode}</div>
                      <div className="flex items-center space-x-1">
                        <Users className="w-3 h-3 text-gray-400" />
                        <span>추천: {node.totalReferrals}명</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <TrendingUp className="w-3 h-3 text-gray-400" />
                        <span>거래량: {(node.totalVolume / 1000).toFixed(0)}K VTTN</span>
                      </div>
                      {node.totalReferrals > 100 && (
                        <div className="mt-1 px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-bold">
                          ⚠️ 의심 계정
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReferralPyramid;

